import { captureApiError, classifyApiError, isSilentApiError } from '@affiliate-cabinet/rest'
import { onError } from '@apollo/client/link/error'

import { captureGraphQLError } from '../captureGraphQLError'

/**
 * Centralized error handler for all Apollo queries and mutations.
 *
 * Decision table:
 *  graphql      → Sentry with tag api_error_kind=graphql (resolver / validation errors)
 *  abort        → drop silently (no Sentry, no notification)
 *  unauthorized → drop silently (redirect handled by AuthProvider)
 *  rate_limit   → drop silently
 *  network      → Sentry
 *  forbidden    → Sentry with context tag
 *  client       → Sentry with context tag
 *  server       → Sentry with context tag
 *  parse        → Sentry with context tag (contract / integration bug)
 *  unknown      → Sentry with context tag
 */
const handleApolloError: Parameters<typeof onError>[0] = ({ graphQLErrors, networkError }) => {
  if (graphQLErrors?.length) {
    for (const graphQLError of graphQLErrors) {
      captureGraphQLError(graphQLError)
    }
  }

  if (networkError) {
    const { kind, status } = classifyApiError(networkError)

    if (isSilentApiError(kind)) {
      return
    }

    captureApiError(networkError, { kind, status })
  }
}

export const createApolloErrorLink = (): ReturnType<typeof onError> => onError(handleApolloError)

export const apolloErrorLink = createApolloErrorLink()
